import { create } from 'zustand'
import { findBestSnap } from '../assembly/snapping/snap-engine'
import { findOverlappingPart, resolveDownwardContact } from '../assembly/collision/collision-engine'
import { getPartById } from '../catalog/part-catalog'
import {
  battlebotAssembly,
  battlebotMiniAssembly,
  starterAssembly,
  type PlacedPart,
  type StudioEnvironment,
  type StudioMode,
} from '../domain/assembly'
import {
  battlebotConnections,
  battlebotMiniConnections,
  normalizeConnectionJointTypes,
  type AssemblyConnection,
} from '../domain/connection'
import {
  DEFAULT_CODING_PROGRAM_NAME,
  createDefaultCodingProgram,
  normalizeCodingProgram,
  type CodingProgramBlock,
} from '../domain/coding'
import type { MotorControl, MotorDirection } from '../domain/motor-control'
import {
  connectedInstanceIds,
  normalizeRobotGroups,
  robotGroupForPart,
  type RobotGroup,
} from '../domain/robot-group'
import { findImpactFailure } from '../domain/simulation-analysis'
import type { ObstacleDraft, SimulationObstacle } from '../domain/simulation-obstacle'
import { assemblyRepository } from '../services/assemblyRepository'
import { studioSessionRepository } from '../services/studioSessionRepository'

type StudioSnapshot = {
  parts: PlacedPart[]
  connections: AssemblyConnection[]
  robotGroups: RobotGroup[]
}

type SimulationTransform = {
  position: PlacedPart['position']
  rotation: PlacedPart['rotation']
}

type StudioState = {
  mode: StudioMode
  environment: StudioEnvironment
  selectedPartId: string
  selectedInstanceId: string | null
  selectedInstanceIds: string[]
  placedParts: PlacedPart[]
  connections: AssemblyConnection[]
  robotGroups: RobotGroup[]
  motorControls: Record<string, MotorControl>
  runtimeMotorControls: Record<string, MotorControl> | null
  simulationObstacles: SimulationObstacle[]
  codingProgramName: string
  codingProgram: CodingProgramBlock[]
  partLimit: number | null
  autoSnap: boolean
  gravity: boolean
  preventOverlap: boolean
  solidFloor: boolean
  isSimulating: boolean
  notice: string | null
  history: StudioSnapshot[]
  future: StudioSnapshot[]
  setMode: (mode: StudioMode) => void
  setEnvironment: (environment: StudioEnvironment) => void
  setPartLimit: (partLimit: number | null) => void
  selectPart: (partId: string) => void
  selectInstance: (instanceId: string | null, additive?: boolean) => void
  selectInstances: (instanceIds: string[]) => void
  selectAllInstances: () => void
  groupSelected: () => void
  ungroupSelected: () => void
  addSelectedPart: () => void
  addPartAt: (partId: string, position: PlacedPart['position']) => void
  duplicateSelectedPart: () => void
  removeSelectedPart: () => void
  rotateSelectedPart: () => void
  rotateSelectedAxis: (axis: 'x' | 'y' | 'z', delta: number) => void
  moveSelectedPart: (deltaX: number, deltaZ: number) => void
  moveSelectedVertical: (delta: number) => void
  setSelectedPosition: (axis: 'x' | 'y' | 'z', value: number) => void
  beginSelectedTransform: () => void
  setSelectedTransform: (
    position: PlacedPart['position'],
    rotation: PlacedPart['rotation'],
  ) => void
  disconnectSelected: () => void
  resetAssembly: () => void
  clearAssembly: () => void
  loadBattlebot: () => void
  loadBattlebotMini: () => void
  toggleAutoSnap: () => void
  toggleGravity: () => void
  togglePreventOverlap: () => void
  toggleSolidFloor: () => void
  setMotorDirection: (cubeInstanceId: string, direction: MotorDirection) => void
  setMotorSpeed: (cubeInstanceId: string, speedPercent: number) => void
  setRuntimeMotorControls: (controls: Record<string, MotorControl> | null) => void
  setCodingProgramName: (name: string) => void
  setCodingProgram: (
    update: CodingProgramBlock[] | ((program: CodingProgramBlock[]) => CodingProgramBlock[]),
  ) => void
  addSimulationObstacle: (draft: ObstacleDraft) => void
  removeSimulationObstacle: (obstacleId: string) => void
  moveSimulationObstacle: (obstacleId: string, deltaX: number, deltaZ: number) => void
  moveConnectedGroup: (
    instanceIds: string[],
    deltaX: number,
    deltaZ: number,
    deltaY?: number,
  ) => void
  rotateConnectedGroup: (transforms: Record<string, SimulationTransform>) => void
  applySimulationImpact: (
    memberIds: string[],
    impactPoint: { x: number; z: number },
    impactEnergyJ: number,
    runtimeTransforms: Record<string, SimulationTransform>,
    obstacleLabel: string,
  ) => void
  toggleSimulation: () => void
  undo: () => void
  redo: () => void
  save: () => void
  load: () => void
  clearNotice: () => void
}

const checkpoint = (state: StudioState) => ({
  history: [...state.history.slice(-19), {
    parts: state.placedParts,
    connections: state.connections,
    robotGroups: state.robotGroups,
  }],
  future: [],
})

function selectionMembers(state: StudioState, instanceId: string) {
  return robotGroupForPart(state.robotGroups, instanceId)?.instanceIds ?? [instanceId]
}

function uniqueExistingIds(state: StudioState, instanceIds: string[]) {
  const requested = new Set(instanceIds)
  const expanded = new Set<string>()
  for (const instanceId of requested) {
    selectionMembers(state, instanceId).forEach((id) => expanded.add(id))
  }
  return state.placedParts
    .map((part) => part.instanceId)
    .filter((id) => expanded.has(id))
}

function keepInternalConnections(
  connections: AssemblyConnection[],
  transformedIds: Set<string>,
) {
  return connections.filter((connection) => {
    const fromSelected = transformedIds.has(connection.fromPartId)
    const toSelected = transformedIds.has(connection.toPartId)
    return fromSelected === toSelected
  })
}

function rotatePoint(
  point: PlacedPart['position'],
  center: PlacedPart['position'],
  axis: 'x' | 'y' | 'z',
  angle: number,
) {
  const x = point.x - center.x
  const y = point.y - center.y
  const z = point.z - center.z
  const cosine = Math.cos(angle)
  const sine = Math.sin(angle)
  if (axis === 'x') return {
    x: point.x,
    y: center.y + y * cosine - z * sine,
    z: center.z + y * sine + z * cosine,
  }
  if (axis === 'y') return {
    x: center.x + x * cosine + z * sine,
    y: point.y,
    z: center.z - x * sine + z * cosine,
  }
  return {
    x: center.x + x * cosine - y * sine,
    y: center.y + x * sine + y * cosine,
    z: point.z,
  }
}

function transformedPartsOverlapStaticParts(
  parts: PlacedPart[],
  transformedIds: Set<string>,
) {
  const staticParts = parts.filter((part) => !transformedIds.has(part.instanceId))
  return parts
    .filter((part) => transformedIds.has(part.instanceId))
    .some((part) => findOverlappingPart(part, [...staticParts, part]))
}

function rotateSelectedParts(
  state: StudioState,
  axis: 'x' | 'y' | 'z',
  angle: number,
) {
  const selectedIds = new Set(state.selectedInstanceIds)
  const members = state.placedParts.filter((part) => selectedIds.has(part.instanceId))
  const center = members.reduce((sum, part) => ({
    x: sum.x + part.position.x / members.length,
    y: sum.y + part.position.y / members.length,
    z: sum.z + part.position.z / members.length,
  }), { x: 0, y: 0, z: 0 })
  const rotatedParts = state.placedParts.map((part) => {
    if (!selectedIds.has(part.instanceId)) return part
    const position = members.length > 1
      ? rotatePoint(part.position, center, axis, angle)
      : part.position
    return {
      ...part,
      position: {
        x: Number(position.x.toFixed(6)),
        y: Number(position.y.toFixed(6)),
        z: Number(position.z.toFixed(6)),
      },
      rotation: {
        ...part.rotation,
        [axis]: Number((part.rotation[axis] + angle).toFixed(6)),
      },
    }
  })
  return { selectedIds, rotatedParts }
}

function snapPartIfPossible(
  state: StudioState,
  parts: PlacedPart[],
  instanceId: string | null,
) {
  if (!instanceId) return { parts, connections: state.connections, snapped: false, motor: false }
  const remainingConnections = state.connections.filter((connection) =>
    connection.fromPartId !== instanceId && connection.toPartId !== instanceId)
  const movingPart = parts.find((part) => part.instanceId === instanceId)
  if (!movingPart) return { parts, connections: remainingConnections, snapped: false, motor: false }
  const result = findBestSnap(movingPart, parts, remainingConnections)
  if (!result) return { parts, connections: remainingConnections, snapped: false, motor: false }
  return {
    parts: parts.map((part) => part.instanceId === instanceId
      ? { ...part, position: result.position, rotation: result.rotation }
      : part),
    connections: [...remainingConnections, ...result.connections],
    snapped: true,
    motor: result.connection.fromConnectorId.includes('motor')
      || result.connection.toConnectorId.includes('motor'),
  }
}

const restoredSession = studioSessionRepository.load()
const restoredParts = restoredSession?.parts ?? starterAssembly
const restoredConnections = normalizeConnectionJointTypes(restoredSession?.connections ?? [])
const restoredRobotGroups = normalizeRobotGroups(
  restoredSession?.robotGroups ?? [],
  restoredParts,
  restoredConnections,
)
const restoredInstanceIds = new Set(restoredParts.map((part) => part.instanceId))
const restoredSelectedInstanceId = restoredSession?.selection.selectedInstanceId
  && restoredInstanceIds.has(restoredSession.selection.selectedInstanceId)
  ? restoredSession.selection.selectedInstanceId
  : null
const restoredSelectedInstanceIds = restoredSession?.selection.selectedInstanceIds
  .filter((instanceId) => restoredInstanceIds.has(instanceId))
  ?? (restoredSelectedInstanceId ? [restoredSelectedInstanceId] : [])
const restoredSelectedPartId = restoredSession
  && getPartById(restoredSession.selection.selectedPartId)
  ? restoredSession.selection.selectedPartId
  : 'part-cube-stl-step'

export const useStudioStore = create<StudioState>((set, get) => ({
  mode: restoredSession?.settings.mode ?? 'assembly',
  environment: restoredSession?.settings.environment ?? 'flat',
  selectedPartId: restoredSelectedPartId,
  selectedInstanceId: restoredSession
    ? restoredSelectedInstanceId
    : starterAssembly[0].instanceId,
  selectedInstanceIds: restoredSession
    ? restoredSelectedInstanceIds
    : [starterAssembly[0].instanceId],
  placedParts: restoredParts,
  connections: restoredConnections,
  robotGroups: restoredRobotGroups,
  motorControls: restoredSession?.motorControls ?? {},
  runtimeMotorControls: null,
  simulationObstacles: restoredSession?.simulationObstacles ?? [],
  codingProgramName: restoredSession?.codingProgramName ?? DEFAULT_CODING_PROGRAM_NAME,
  codingProgram: normalizeCodingProgram(restoredSession?.codingProgram ?? createDefaultCodingProgram()),
  partLimit: null,
  autoSnap: restoredSession?.settings.autoSnap ?? true,
  gravity: restoredSession?.settings.gravity ?? true,
  preventOverlap: restoredSession?.settings.preventOverlap ?? true,
  solidFloor: restoredSession?.settings.solidFloor ?? true,
  isSimulating: false,
  notice: null,
  history: [],
  future: [],
  setMode: (mode) => set((state) => ({
    mode,
    isSimulating: mode === 'simulation' ? state.isSimulating : false,
    runtimeMotorControls: mode === 'simulation' ? state.runtimeMotorControls : null,
  })),
  setEnvironment: (environment) => set({
    environment,
    notice: environment === 'flat'
      ? '기본 평면으로 변경했습니다.'
      : environment === 'ramp'
        ? '경사로 환경으로 변경했습니다.'
        : environment === 'road'
          ? '레이싱 트랙으로 변경했습니다.'
          : '장애물 코스로 변경했습니다.',
  }),
  setPartLimit: (partLimit) => set({ partLimit }),
  selectPart: (selectedPartId) => set({ selectedPartId }),
  selectInstance: (selectedInstanceId, additive = false) => set((state) => {
    if (!selectedInstanceId) return { selectedInstanceId: null, selectedInstanceIds: [] }
    const members = selectionMembers(state, selectedInstanceId)
    if (!additive) return { selectedInstanceId, selectedInstanceIds: members }
    const selected = new Set(state.selectedInstanceIds)
    const isSelected = members.every((id) => selected.has(id))
    members.forEach((id) => isSelected ? selected.delete(id) : selected.add(id))
    const selectedInstanceIds = state.placedParts
      .map((part) => part.instanceId)
      .filter((id) => selected.has(id))
    return {
      selectedInstanceIds,
      selectedInstanceId: isSelected
        ? selectedInstanceIds.at(-1) ?? null
        : selectedInstanceId,
    }
  }),
  selectInstances: (instanceIds) => set((state) => {
    const selectedInstanceIds = uniqueExistingIds(state, instanceIds)
    return {
      selectedInstanceIds,
      selectedInstanceId: selectedInstanceIds.at(-1) ?? null,
      notice: selectedInstanceIds.length > 1
        ? `영역에서 부품 ${selectedInstanceIds.length}개를 선택했습니다.`
        : state.notice,
    }
  }),
  selectAllInstances: () => set((state) => ({
    selectedInstanceIds: state.placedParts.map((part) => part.instanceId),
    selectedInstanceId: state.placedParts.at(-1)?.instanceId ?? null,
  })),
  groupSelected: () => set((state) => {
    const selectedIds = uniqueExistingIds(state, state.selectedInstanceIds)
    if (selectedIds.length < 2) {
      return { notice: '연결된 부품을 두 개 이상 선택해야 로봇으로 그룹화할 수 있습니다.' }
    }
    const connectedIds = connectedInstanceIds(
      state.placedParts,
      state.connections,
      selectedIds[0],
    )
    const connected = new Set(connectedIds)
    if (!selectedIds.every((id) => connected.has(id))) {
      return { notice: '실제 커넥터로 연결되지 않은 부품은 같은 로봇으로 그룹화할 수 없습니다.' }
    }
    if (connectedIds.length < 2) {
      return { notice: '선택한 부품 사이에 실제 연결이 없습니다.' }
    }
    const memberSet = new Set(connectedIds)
    const robotGroups = state.robotGroups.filter((group) =>
      !group.instanceIds.some((id) => memberSet.has(id)))
    const group: RobotGroup = {
      id: `robot-group-${crypto.randomUUID()}`,
      name: `로봇 ${robotGroups.length + 1}`,
      instanceIds: connectedIds,
    }
    return {
      ...checkpoint(state),
      robotGroups: [...robotGroups, group],
      selectedInstanceId: selectedIds.at(-1) ?? connectedIds[0],
      selectedInstanceIds: connectedIds,
      notice: `실제로 연결된 부품 ${connectedIds.length}개를 ${group.name}으로 그룹화했습니다.`,
    }
  }),
  ungroupSelected: () => set((state) => {
    const selected = new Set(state.selectedInstanceIds)
    const removed = state.robotGroups.filter((group) =>
      group.instanceIds.some((id) => selected.has(id)))
    if (!removed.length) return { notice: '선택한 부품은 로봇 그룹에 포함되어 있지 않습니다.' }
    const removedIds = new Set(removed.map((group) => group.id))
    return {
      ...checkpoint(state),
      robotGroups: state.robotGroups.filter((group) => !removedIds.has(group.id)),
      notice: `${removed.map((group) => group.name).join(', ')} 그룹을 해제했습니다.`,
    }
  }),
  addSelectedPart: () => set((state) => {
    if (state.partLimit !== null && state.placedParts.length >= state.partLimit) {
      return { notice: `이 미션에서 사용할 수 있는 부품은 ${state.partLimit}개입니다.` }
    }
    const index = state.placedParts.length
    const instanceId = `${state.selectedPartId}-${crypto.randomUUID()}`
    const position = { x: ((index % 4) - 1.5) * 1.15, y: 0.65, z: 2.25 + Math.floor(index / 4) * 1.15 }
    return {
      ...checkpoint(state),
      placedParts: [...state.placedParts, { instanceId, partId: state.selectedPartId, position, rotation: { x: 0, y: 0, z: 0 } }],
      selectedInstanceId: instanceId,
      selectedInstanceIds: [instanceId],
      notice: '작업실에 부품을 추가했습니다.',
    }
  }),
  addPartAt: (partId, position) => set((state) => {
    if (state.partLimit !== null && state.placedParts.length >= state.partLimit) {
      return { notice: `이 미션에서 사용할 수 있는 부품은 ${state.partLimit}개입니다.` }
    }
    const instanceId = `${partId}-${crypto.randomUUID()}`
    return {
      ...checkpoint(state),
      selectedPartId: partId,
      placedParts: [...state.placedParts, {
        instanceId,
        partId,
        position,
        rotation: { x: 0, y: 0, z: 0 },
      }],
      selectedInstanceId: instanceId,
      selectedInstanceIds: [instanceId],
      notice: '놓은 위치에 부품을 추가했습니다.',
    }
  }),
  duplicateSelectedPart: () => set((state) => {
    const sources = state.placedParts.filter((part) => state.selectedInstanceIds.includes(part.instanceId))
    if (!sources.length) return state
    if (state.partLimit !== null && state.placedParts.length + sources.length > state.partLimit) {
      return { notice: `이 미션에서 사용할 수 있는 부품은 ${state.partLimit}개입니다.` }
    }
    const duplicateIds = new Map(sources.map((source) => [
      source.instanceId,
      `${source.partId}-${crypto.randomUUID()}`,
    ]))
    const duplicates = sources.map((source) => ({
      ...source,
      instanceId: duplicateIds.get(source.instanceId)!,
      position: { ...source.position, x: source.position.x + 1.15 },
    }))
    const duplicatedConnections = state.connections.flatMap((connection) => {
      const fromPartId = duplicateIds.get(connection.fromPartId)
      const toPartId = duplicateIds.get(connection.toPartId)
      return fromPartId && toPartId
        ? [{ ...connection, id: `${connection.id}-${crypto.randomUUID()}`, fromPartId, toPartId }]
        : []
    })
    const sourceGroup = state.robotGroups.find((group) =>
      group.instanceIds.length === sources.length
      && group.instanceIds.every((id) => duplicateIds.has(id)))
    const duplicatedGroup: RobotGroup[] = sourceGroup
      ? [{
          id: `robot-group-${crypto.randomUUID()}`,
          name: `로봇 ${state.robotGroups.length + 1}`,
          instanceIds: sourceGroup.instanceIds.map((id) => duplicateIds.get(id)!),
        }]
      : []
    return {
      ...checkpoint(state),
      placedParts: [...state.placedParts, ...duplicates],
      connections: [...state.connections, ...duplicatedConnections],
      robotGroups: [...state.robotGroups, ...duplicatedGroup],
      selectedInstanceId: duplicates.at(-1)!.instanceId,
      selectedInstanceIds: duplicates.map((part) => part.instanceId),
      notice: sourceGroup ? '연결과 그룹을 유지한 로봇 복제본을 만들었습니다.' : '선택한 부품을 복제했습니다.',
    }
  }),
  removeSelectedPart: () => set((state) => {
    if (!state.selectedInstanceIds.length) return state
    const removedIds = new Set(state.selectedInstanceIds)
    const placedParts = state.placedParts.filter((part) => !removedIds.has(part.instanceId))
    const connections = state.connections.filter((connection) =>
      !removedIds.has(connection.fromPartId) && !removedIds.has(connection.toPartId))
    return {
      ...checkpoint(state),
      placedParts,
      connections,
      robotGroups: normalizeRobotGroups(state.robotGroups, placedParts, connections),
      selectedInstanceId: null,
      selectedInstanceIds: [],
      notice: '선택한 부품을 삭제했습니다.',
    }
  }),
  rotateSelectedPart: () => set((state) => {
    if (!state.selectedInstanceIds.length) return state
    const { selectedIds, rotatedParts } = rotateSelectedParts(state, 'y', Math.PI / 2)
    if (state.preventOverlap && transformedPartsOverlapStaticParts(rotatedParts, selectedIds)) {
      return { notice: '다른 부품과 겹치는 방향으로 회전할 수 없습니다.' }
    }
    return {
      ...checkpoint(state),
      placedParts: rotatedParts,
      connections: keepInternalConnections(state.connections, selectedIds),
      notice: selectedIds.size > 1 ? '로봇 전체를 90° 회전했습니다.' : '부품을 90° 회전했습니다.',
    }
  }),
  rotateSelectedAxis: (axis, delta) => set((state) => {
    if (!state.selectedInstanceIds.length) return state
    const angle = (state.autoSnap ? Math.PI / 12 : Math.PI / 36) * delta
    const { selectedIds, rotatedParts } = rotateSelectedParts(state, axis, angle)
    if (state.preventOverlap && transformedPartsOverlapStaticParts(rotatedParts, selectedIds)) {
      return { notice: '다른 부품과 겹치는 방향으로 회전할 수 없습니다.' }
    }
    return {
      ...checkpoint(state),
      placedParts: rotatedParts,
      connections: keepInternalConnections(state.connections, selectedIds),
      notice: `${axis.toUpperCase()}축으로 ${delta > 0 ? '+' : '-'}${state.autoSnap ? 15 : 5}° 회전했습니다.`,
    }
  }),
  moveSelectedPart: (deltaX, deltaZ) => set((state) => {
    if (!state.selectedInstanceId) return state
    const step = state.autoSnap ? 0.25 : 0.05
    const movedParts = state.placedParts.map((part) => state.selectedInstanceIds.includes(part.instanceId)
      ? {
          ...part,
          position: {
            ...part.position,
            x: Number((part.position.x + deltaX * step).toFixed(2)),
            z: Number((part.position.z + deltaZ * step).toFixed(2)),
          },
        }
      : part)
    const snapped = state.selectedInstanceIds.length === 1
      ? snapPartIfPossible(state, movedParts, state.selectedInstanceId)
      : { parts: movedParts, connections: state.connections, snapped: false, motor: false }
    const movingIds = new Set(state.selectedInstanceIds)
    if (state.preventOverlap && transformedPartsOverlapStaticParts(snapped.parts, movingIds)) {
      return { notice: '다른 부품과 겹치는 위치에는 놓을 수 없습니다.' }
    }
    return {
      ...checkpoint(state),
      placedParts: snapped.parts,
      connections: snapped.connections,
      robotGroups: normalizeRobotGroups(state.robotGroups, snapped.parts, snapped.connections),
      notice: snapped.snapped
        ? snapped.motor
          ? '모터 수형이 큐브 모터 암형 홈에 체결되었습니다.'
          : '호환되는 커넥터에 스냅했습니다.'
        : state.notice,
    }
  }),
  moveSelectedVertical: (delta) => set((state) => {
    if (!state.selectedInstanceIds.length) return state
    const step = state.autoSnap ? 0.25 : 0.05
    const selectedIds = new Set(state.selectedInstanceIds)
    let movedParts = state.placedParts.map((part) => {
      if (!selectedIds.has(part.instanceId)) return part
      const definition = getPartById(part.partId)
      const minimumY = (definition?.dimensions[1] ?? 0.01) * 10
      return {
        ...part,
        position: {
          ...part.position,
          y: Number(Math.max(minimumY, part.position.y + delta * step).toFixed(2)),
        },
      }
    })
    const movingPart = movedParts.find((part) => part.instanceId === state.selectedInstanceId)
    if (state.preventOverlap && transformedPartsOverlapStaticParts(movedParts, selectedIds)) {
      if (selectedIds.size > 1) return { notice: '다른 부품과 겹치는 높이로 로봇을 이동할 수 없습니다.' }
      const previous = state.placedParts.find((part) => part.instanceId === movingPart?.instanceId)
      const resolved = previous && movingPart
        ? resolveDownwardContact(movingPart, movedParts, previous.position.y)
        : null
      if (!resolved) return { notice: '다른 부품과 겹치는 높이로 이동할 수 없습니다.' }
      movedParts = movedParts.map((part) => part.instanceId === resolved.instanceId ? resolved : part)
    }
    return {
      ...checkpoint(state),
      placedParts: movedParts,
      connections: keepInternalConnections(state.connections, selectedIds),
      notice: delta > 0 ? '선택한 부품을 위로 이동했습니다.' : '선택한 부품을 아래로 이동했습니다.',
    }
  }),
  setSelectedPosition: (axis, value) => set((state) => {
    if (!state.selectedInstanceId || !Number.isFinite(value)) return state
    const selected = state.placedParts.find((part) => part.instanceId === state.selectedInstanceId)
    if (!selected) return state
    const selectedIds = new Set(state.selectedInstanceIds)
    let delta = value - selected.position[axis]
    if (axis === 'y') {
      const minimumDelta = state.placedParts
        .filter((part) => selectedIds.has(part.instanceId))
        .reduce((required, part) => {
          const definition = getPartById(part.partId)
          const minimumY = (definition?.dimensions[1] ?? 0.01) * 10
          return Math.max(required, minimumY - part.position.y)
        }, Number.NEGATIVE_INFINITY)
      delta = Math.max(delta, minimumDelta)
    }
    const nextParts = state.placedParts.map((part) => selectedIds.has(part.instanceId)
      ? {
          ...part,
          position: {
            ...part.position,
            [axis]: Number((part.position[axis] + delta).toFixed(2)),
          },
        }
      : part)
    if (state.preventOverlap && transformedPartsOverlapStaticParts(nextParts, selectedIds)) {
      return { notice: '다른 부품과 겹치는 위치는 입력할 수 없습니다.' }
    }
    return {
      ...checkpoint(state),
      placedParts: nextParts,
      connections: keepInternalConnections(state.connections, selectedIds),
      notice: selectedIds.size > 1 ? '로봇 전체의 위치를 변경했습니다.' : '부품 위치를 변경했습니다.',
    }
  }),
  beginSelectedTransform: () => set((state) => {
    if (!state.selectedInstanceId) return state
    return checkpoint(state)
  }),
  setSelectedTransform: (position, rotation) => set((state) => {
    let transformed = state.placedParts.map((part) => part.instanceId === state.selectedInstanceId
      ? { ...part, position, rotation }
      : part)
    const snapped = snapPartIfPossible(state, transformed, state.selectedInstanceId)
    transformed = snapped.parts
    const movingPart = transformed.find((part) => part.instanceId === state.selectedInstanceId)
    if (!snapped.snapped && state.preventOverlap && movingPart && findOverlappingPart(movingPart, transformed)) {
      const previous = state.placedParts.find((part) => part.instanceId === movingPart?.instanceId)
      const resolved = previous ? resolveDownwardContact(movingPart, transformed, previous.position.y) : null
      if (!resolved) {
        return {
          placedParts: state.placedParts,
          connections: state.connections,
          notice: '다른 부품과 겹치는 위치에는 놓을 수 없습니다.',
        }
      }
      transformed = transformed.map((part) => part.instanceId === resolved.instanceId ? resolved : part)
    }
    return {
      placedParts: transformed,
      connections: snapped.connections,
      robotGroups: normalizeRobotGroups(state.robotGroups, transformed, snapped.connections),
      notice: snapped.snapped
        ? snapped.motor
          ? '모터 수형이 큐브 모터 암형 홈에 체결되었습니다.'
          : '호환되는 커넥터에 스냅했습니다.'
        : state.notice,
    }
  }),
  disconnectSelected: () => set((state) => {
    if (!state.selectedInstanceIds.length) return state
    const selected = new Set(state.selectedInstanceIds)
    const connections = state.connections.filter((connection) =>
      !selected.has(connection.fromPartId) && !selected.has(connection.toPartId))
    if (connections.length === state.connections.length) return { notice: '선택한 부품에 연결이 없습니다.' }
    return {
      ...checkpoint(state),
      connections,
      robotGroups: normalizeRobotGroups(state.robotGroups, state.placedParts, connections),
      notice: '선택한 부품의 연결을 해제했습니다.',
    }
  }),
  resetAssembly: () => set((state) => ({
    ...checkpoint(state),
    placedParts: starterAssembly,
    connections: [],
    robotGroups: [],
    selectedInstanceId: starterAssembly[0].instanceId,
    selectedInstanceIds: [starterAssembly[0].instanceId],
    notice: '시작 조립물로 초기화했습니다.',
  })),
  clearAssembly: () => set((state) => ({
    ...checkpoint(state),
    placedParts: [],
    connections: [],
    robotGroups: [],
    motorControls: {},
    runtimeMotorControls: null,
    selectedInstanceId: null,
    selectedInstanceIds: [],
    isSimulating: false,
    notice: '조립 화면의 모든 부품을 없앴습니다.',
  })),
  loadBattlebot: () => set((state) => ({
    ...checkpoint(state),
    placedParts: battlebotAssembly,
    connections: battlebotConnections,
    robotGroups: [{
      id: 'robot-group-battlebot',
      name: '배틀봇',
      instanceIds: battlebotAssembly.map((part) => part.instanceId),
    }],
    selectedPartId: 'part-cube-stl-step',
    selectedInstanceId: null,
    selectedInstanceIds: [],
    notice: 'MD-01 증속기가 적용된 배틀봇 조립을 불러왔습니다.',
  })),
  loadBattlebotMini: () => set((state) => ({
    ...checkpoint(state),
    placedParts: battlebotMiniAssembly,
    connections: battlebotMiniConnections,
    robotGroups: [{
      id: 'robot-group-battlebot-mini',
      name: '배틀봇 미니',
      instanceIds: battlebotMiniAssembly.map((part) => part.instanceId),
    }],
    selectedPartId: 'part-cube-stl-step',
    selectedInstanceId: null,
    selectedInstanceIds: [],
    notice: '바퀴가 큐브에 직접 연결된 배틀봇 미니 조립을 불러왔습니다.',
  })),
  toggleAutoSnap: () => set((state) => ({ autoSnap: !state.autoSnap })),
  toggleGravity: () => set((state) => ({ gravity: !state.gravity })),
  togglePreventOverlap: () => set((state) => ({ preventOverlap: !state.preventOverlap })),
  toggleSolidFloor: () => set((state) => ({ solidFloor: !state.solidFloor })),
  setMotorDirection: (cubeInstanceId, direction) => set((state) => ({
    motorControls: {
      ...state.motorControls,
      [cubeInstanceId]: {
        direction,
        speedPercent: state.motorControls[cubeInstanceId]?.speedPercent ?? 20,
      },
    },
  })),
  setMotorSpeed: (cubeInstanceId, speedPercent) => set((state) => ({
    motorControls: {
      ...state.motorControls,
      [cubeInstanceId]: {
        direction: state.motorControls[cubeInstanceId]?.direction ?? 'stop',
        speedPercent: Math.min(100, Math.max(0, Math.round(speedPercent))),
      },
    },
  })),
  setRuntimeMotorControls: (runtimeMotorControls) => set({ runtimeMotorControls }),
  setCodingProgramName: (name) => set({ codingProgramName: name.slice(0, 30) }),
  setCodingProgram: (update) => set((state) => ({
    codingProgram: typeof update === 'function' ? update(state.codingProgram) : update,
  })),
  addSimulationObstacle: (draft) => set((state) => {
    const finiteOr = (value: number, fallback: number) =>
      Number.isFinite(value) ? value : fallback
    const size = {
      x: Math.min(0.5, Math.max(0.01, finiteOr(draft.sizeCm.x, 5) / 100)),
      y: Math.min(0.5, Math.max(0.01, finiteOr(draft.sizeCm.y, 5) / 100)),
      z: Math.min(0.5, Math.max(0.01, finiteOr(draft.sizeCm.z, 5) / 100)),
    }
    const index = state.simulationObstacles.length
    const column = index % 3
    const row = Math.floor(index / 3)
    const obstacle: SimulationObstacle = {
      id: `simulation-obstacle-${crypto.randomUUID()}`,
      label: draft.preset === 'custom'
        ? `사용자 장애물 ${index + 1}`
        : `${draft.preset === 'small' ? '소' : draft.preset === 'medium' ? '중' : '대'} 장애물 ${index + 1}`,
      preset: draft.preset,
      size,
      massKg: Math.min(20, Math.max(0.001, finiteOr(draft.massG, 50) / 1000)),
      position: {
        x: (column - 1) * 2.5,
        y: size.y * 10,
        z: -2.8 - row * 2,
      },
    }
    return {
      simulationObstacles: [...state.simulationObstacles, obstacle],
      notice: `${obstacle.label}을 현재 코스에 추가했습니다.`,
    }
  }),
  removeSimulationObstacle: (obstacleId) => set((state) => ({
    simulationObstacles: state.simulationObstacles.filter((obstacle) => obstacle.id !== obstacleId),
    notice: '장애물을 제거했습니다.',
  })),
  moveSimulationObstacle: (obstacleId, deltaX, deltaZ) => set((state) => ({
    simulationObstacles: state.simulationObstacles.map((obstacle) =>
      obstacle.id === obstacleId
        ? {
            ...obstacle,
            position: {
              ...obstacle.position,
              x: Number((obstacle.position.x + deltaX).toFixed(3)),
              z: Number((obstacle.position.z + deltaZ).toFixed(3)),
            },
          }
        : obstacle),
    notice: '장애물을 새 위치로 이동했습니다.',
  })),
  moveConnectedGroup: (instanceIds, deltaX, deltaZ, deltaY = 0) => set((state) => {
    const movingIds = new Set(instanceIds)
    return {
      ...checkpoint(state),
      placedParts: state.placedParts.map((part) => movingIds.has(part.instanceId)
        ? {
            ...part,
            position: {
              ...part.position,
              x: Number((part.position.x + deltaX).toFixed(3)),
              y: Number((part.position.y + deltaY).toFixed(3)),
              z: Number((part.position.z + deltaZ).toFixed(3)),
            },
          }
        : part),
      notice: instanceIds.length > 1
        ? `연결된 부품 ${instanceIds.length}개를 하나의 로봇으로 이동했습니다.`
        : '부품을 이동했습니다.',
    }
  }),
  rotateConnectedGroup: (transforms) => set((state) => {
    const transformedIds = new Set(Object.keys(transforms))
    if (!transformedIds.size) return state
    return {
      ...checkpoint(state),
      placedParts: state.placedParts.map((part) => {
        const transform = transforms[part.instanceId]
        return transform
          ? {
              ...part,
              position: { ...transform.position },
              rotation: { ...transform.rotation },
            }
          : part
      }),
      notice: transformedIds.size > 1
        ? `연결된 부품 ${transformedIds.size}개를 하나의 로봇으로 회전했습니다.`
        : '부품을 회전했습니다.',
    }
  }),
  applySimulationImpact: (
    memberIds,
    impactPoint,
    impactEnergyJ,
    runtimeTransforms,
    obstacleLabel,
  ) => set((state) => {
    const failure = findImpactFailure(
      state.placedParts,
      state.connections,
      memberIds,
      impactPoint,
      impactEnergyJ,
    )
    if (!failure) {
      return {
        notice: `${obstacleLabel} 충돌 ${Math.round(impactEnergyJ * 1000)}mJ · 연결 유지`,
      }
    }
    const failedPart = state.placedParts.find((part) => part.instanceId === failure.instanceId)
    const failedName = failedPart ? getPartById(failedPart.partId)?.name : null
    const brokenConnections = new Set(failure.connectionIds)
    const connections = state.connections.filter((connection) => !brokenConnections.has(connection.id))
    return {
      ...checkpoint(state),
      placedParts: state.placedParts.map((part) => {
        const runtime = runtimeTransforms[part.instanceId]
        return runtime
          ? {
              ...part,
              position: { ...runtime.position },
              rotation: { ...runtime.rotation },
            }
          : part
      }),
      connections,
      robotGroups: normalizeRobotGroups(state.robotGroups, state.placedParts, connections),
      notice: `${obstacleLabel} 충돌 ${Math.round(impactEnergyJ * 1000)}mJ · ${failedName ?? '취약 부품'} 연결이 분리되었습니다.`,
    }
  }),
  toggleSimulation: () => set((state) => {
    const willStart = !state.isSimulating
    return {
      isSimulating: willStart,
      mode: 'simulation',
      runtimeMotorControls: willStart ? state.runtimeMotorControls : null,
      notice: willStart
        ? '모터 명령으로 조립물 주행을 시작했습니다.'
        : '시뮬레이션을 중지했습니다. 설정을 바꾸고 다시 실행할 수 있습니다.',
    }
  }),
  undo: () => set((state) => {
    const previous = state.history.at(-1)
    if (!previous) return state
    return {
      placedParts: previous.parts,
      connections: previous.connections,
      robotGroups: previous.robotGroups,
      history: state.history.slice(0, -1),
      future: [{
        parts: state.placedParts,
        connections: state.connections,
        robotGroups: state.robotGroups,
      }, ...state.future],
      selectedInstanceId: null,
      selectedInstanceIds: [],
    }
  }),
  redo: () => set((state) => {
    const next = state.future[0]
    if (!next) return state
    return {
      placedParts: next.parts,
      connections: next.connections,
      robotGroups: next.robotGroups,
      history: [...state.history, {
        parts: state.placedParts,
        connections: state.connections,
        robotGroups: state.robotGroups,
      }],
      future: state.future.slice(1),
      selectedInstanceId: null,
      selectedInstanceIds: [],
    }
  }),
  save: () => {
    try {
      assemblyRepository.save({
        version: 1,
        name: '내 핑퐁 조립물',
        updatedAt: new Date().toISOString(),
        parts: get().placedParts,
        connections: get().connections,
        robotGroups: get().robotGroups,
      })
      set({ notice: `조립물과 연결 ${get().connections.length}개를 이 브라우저에 저장했습니다.` })
    } catch {
      set({ notice: '브라우저 저장 공간을 사용할 수 없어 저장하지 못했습니다.' })
    }
  },
  load: () => {
    const assembly = assemblyRepository.load()
    if (!assembly) {
      set({ notice: '저장된 조립물이 없습니다.' })
      return
    }
    const connections = normalizeConnectionJointTypes(assembly.connections)
    set((state) => ({
      ...checkpoint(state),
      placedParts: assembly.parts,
      connections,
      robotGroups: normalizeRobotGroups(assembly.robotGroups, assembly.parts, connections),
      selectedInstanceId: null,
      selectedInstanceIds: [],
      isSimulating: false,
      mode: 'assembly',
      notice: `저장된 조립물과 연결 ${assembly.connections.length}개를 불러왔습니다.`,
    }))
  },
  clearNotice: () => set({ notice: null }),
}))

function persistStudioSession(state: StudioState) {
  try {
    studioSessionRepository.save({
      version: 2,
      parts: state.placedParts,
      connections: state.connections,
      robotGroups: state.robotGroups,
      motorControls: state.motorControls,
      simulationObstacles: state.simulationObstacles,
      codingProgramName: state.codingProgramName,
      codingProgram: state.codingProgram,
      settings: {
        mode: state.mode,
        environment: state.environment,
        autoSnap: state.autoSnap,
        gravity: state.gravity,
        preventOverlap: state.preventOverlap,
        solidFloor: state.solidFloor,
      },
      selection: {
        selectedPartId: state.selectedPartId,
        selectedInstanceId: state.selectedInstanceId,
        selectedInstanceIds: state.selectedInstanceIds,
      },
    })
  } catch {
    // Private browsing and restricted storage should not block the editor.
  }
}

persistStudioSession(useStudioStore.getState())
useStudioStore.subscribe((state, previousState) => {
  const persistentStateChanged = state.mode !== previousState.mode
    || state.environment !== previousState.environment
    || state.placedParts !== previousState.placedParts
    || state.connections !== previousState.connections
    || state.robotGroups !== previousState.robotGroups
    || state.motorControls !== previousState.motorControls
    || state.simulationObstacles !== previousState.simulationObstacles
    || state.codingProgramName !== previousState.codingProgramName
    || state.codingProgram !== previousState.codingProgram
    || state.autoSnap !== previousState.autoSnap
    || state.gravity !== previousState.gravity
    || state.preventOverlap !== previousState.preventOverlap
    || state.solidFloor !== previousState.solidFloor
    || state.selectedPartId !== previousState.selectedPartId
    || state.selectedInstanceId !== previousState.selectedInstanceId
    || state.selectedInstanceIds !== previousState.selectedInstanceIds
  if (persistentStateChanged) persistStudioSession(state)
})
